import React, { useState } from 'react';
import { useField } from 'formik';
import { Eye, EyeOff } from 'lucide-react';

import { Input } from '@dashboard/components/ui/input';
import { cn } from '@dashboard/lib/utils';

import { authInputClassName } from './authRules';

type PasswordInputProps = Omit<React.ComponentProps<'input'>, 'type'>;

export function PasswordInput({ className, id, ...props }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        {...props}
        id={id}
        type={visible ? 'text' : 'password'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={cn(authInputClassName, 'pr-12', className)}
      />
      <button
        type="button"
        onMouseDown={e => e.preventDefault()}
        onClick={() => setVisible(value => !value)}
        aria-controls={id}
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex items-center rounded-r-[10px] px-3.5 text-gray-500 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        {visible ? (
          <EyeOff className="size-4" aria-hidden="true" />
        ) : (
          <Eye className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

type AuthFieldConfig = {
  type?: string;
  name: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  'aria-invalid'?: boolean;
};

function FormikPasswordInput({
  name,
  id,
  hint,
  'aria-invalid': invalid,
  ...props
}: Omit<AuthFieldConfig, 'type'>) {
  const [field, meta] = useField<string>(name);
  const showsError = meta.touched && !!meta.error;

  return (
    <>
      <PasswordInput
        {...field}
        {...props}
        value={field.value ?? ''}
        id={id || name}
        aria-invalid={invalid || showsError ? true : undefined}
      />
      {hint && !showsError && (
        <p className="mt-1 text-xs text-gray-500 dark:text-muted-foreground">{hint}</p>
      )}
    </>
  );
}

export function renderAuthField(field: AuthFieldConfig) {
  if (field.type !== 'password') return null;

  return (
    <FormikPasswordInput
      name={field.name}
      id={field.id}
      autoComplete={field.autoComplete}
      autoFocus={field.autoFocus}
      placeholder={field.placeholder}
      required={field.required}
      disabled={field.disabled}
      hint={field.hint}
      aria-invalid={field['aria-invalid']}
    />
  );
}
